import { useContext, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { useClientUser } from '@/hooks';
import { UserContext } from '@/components/App';
import { CLIENT_BY_ID } from '@/graphql/queries/users';
import { RESOURCE_SCHEDULE_CUTOFF } from '@/constants/resourceScheduling';
import { GET_ORG_PROFILE_ASSIGNMENTS } from '@/components/CorporateUsers/graphql';
import { buildAssignmentsByShipper, summarizeAssignments } from '@/components/CorporateUsers/utils';
import { asContextClient, shipperTariffRoutes } from './utils';

/**
 * Loads a shipper for a corporate admin and builds the UserContext its panels run in. Call it
 * from the admin's context; `shipperContext` swaps `client` to the shipper.
 * @param {string} client_id
 */
export const useShipperScope = (client_id) => {
    const outer = useContext(UserContext);
    const { admin_clients } = outer;
    const { organization_id } = useClientUser();

    // `admin_clients` holds every client in the org, carriers included, so check the role too.
    const isPermitted = useMemo(
        () =>
            !!organization_id &&
            (admin_clients || []).some((c) => c.client_id === client_id && !!c.user?.roles?.['SHIPPER']),
        [organization_id, admin_clients, client_id]
    );

    const { data, loading, error } = useQuery(CLIENT_BY_ID, {
        variables: { client_id, cutoff: RESOURCE_SCHEDULE_CUTOFF },
        skip: !client_id || !isPermitted,
    });

    const shipper = data?.client;

    const shipperContext = useMemo(
        () => ({
            ...outer,
            client: asContextClient(shipper),
            clientId: shipper?.client_id,
            tariffRoutes: shipper && shipperTariffRoutes(shipper.client_id),
        }),
        [outer, shipper]
    );

    return { isPermitted, shipper, loading, error, shipperContext };
};

/** Which delivery profiles auto-assign this shipper. The detail page sits outside CorpAdminContext, so it runs the org query itself. */
export const useShipperProfile = () => {
    const { client } = useContext(UserContext);
    const { organization_id } = useClientUser();

    const { data, loading, error } = useQuery(GET_ORG_PROFILE_ASSIGNMENTS, {
        variables: { organization_id },
        skip: !organization_id,
    });

    const assignment = useMemo(() => {
        if (!client?.client_id) return null;
        return summarizeAssignments(buildAssignmentsByShipper(data?.delivery_profiles)[client.client_id]);
    }, [data, client]);

    return { assignment, loading, error };
};
